// src/ConsumerApp/Screens/Bucket/components/SharedBucketItemCard.js

import React, { useState, useEffect } from 'react';

// Real SVGs from assets/icons
import Bookmark from '../../../../assets/icons/bookmark.svg';
import FillBookmark from '../../../../assets/icons/FillBookmark.svg';
import ToteIcon from '../../../../assets/icons/tote.svg';

// Shared Contexts for Auth & Feedback
import { useToast } from '../../../context/ToastContext';
import { useAuthModal } from '../../../context/AuthModalContext';

const IMAGE_BASE_URL =
  process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || 'https://assets.brickbucket.in/';

export default function SharedBucketItemCard({
  item,
  customerId,
  isSavedItem,
  onToggleSave,
}) {
  const { showToast } = useToast();
  const { openLoginModal } = useAuthModal();

  const id = String(item?.item_id || item?.product_id || item?._id || '');

  // Resilient text fallbacks for both guest & server data schemas
  const name = item?.item_name || item?.product_name || item?.name || 'Material Item';
  const brand = item?.brand || item?.brand_name || '';
  const unitText = item?.uom_name || item?.UOM || item?.uom || '';

  const quantity = Number(item?.quantity) || 1;

  // Out of stock detection across backend schemas
  const isOutOfStock = Boolean(
    item?.out_of_stock === true ||
    item?.is_out_of_stock === true ||
    item?.inventory_status?.toLowerCase() === 'out_of_stock' ||
    item?.stock === 0
  );

  const unitPrice =
    Number(item?.price) ||
    Number(item?.unit_price) ||
    Number(item?.selling_price) ||
    0;
  const itemTotal = Number(item?.item_total) || unitPrice * quantity;
  const itemMrpTotal =
    Number(item?.item_mrp_total) ||
    (Number(item?.mrp) > 0 ? Number(item?.mrp) * quantity : 0);

  const [imgError, setImgError] = useState(false);
  const [localIsSaved, setLocalIsSaved] = useState(Boolean(isSavedItem));
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  useEffect(() => {
    setLocalIsSaved(Boolean(isSavedItem));
  }, [isSavedItem]);

  const handleBookmarkClick = async (e) => {
    e.stopPropagation();

    if (!customerId) {
      showToast('Please log in to save the item.', 'info');
      if (openLoginModal) openLoginModal();
      return;
    }

    if (!onToggleSave || isSaveLoading) return;

    const targetState = !localIsSaved;
    setLocalIsSaved(targetState);
    setIsSaveLoading(true);

    try {
      const isSuccess = await onToggleSave(item, targetState);
      if (!isSuccess) {
        setLocalIsSaved(!targetState);
      }
    } catch {
      setLocalIsSaved(!targetState);
    } finally {
      setIsSaveLoading(false);
    }
  };

  // Safe Image URL resolution
  const rawImg =
    item?.item_image ||
    item?.product_image ||
    item?.product_image_url ||
    item?.image;

  let cleanImg = rawImg || '';
  if (cleanImg.startsWith('/')) {
    cleanImg = cleanImg.slice(1);
  }

  const imageUrl = cleanImg
    ? cleanImg.startsWith('http://') ||
      cleanImg.startsWith('https://') ||
      cleanImg.startsWith('data:')
      ? cleanImg
      : `${IMAGE_BASE_URL}${cleanImg}`
    : null;

  return (
    <div
      className="bb-bucket-item-card"
      style={{
        backgroundColor: isOutOfStock ? '#FAF8F8' : '#FFFFFF',
      }}
    >
      <style>{`
        .bb-bucket-item-card {
          border-bottom: 1px solid #F1F5F9;
          padding: 12px 14px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          position: relative;
          box-sizing: border-box;
          width: 100%;
          transition: background-color 0.15s ease;
        }

        .bb-bucket-item-card:last-child {
          border-bottom: none;
        }

        .bb-bic-top-tier {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          width: 100%;
        }

        .bb-bic-img-wrapper {
          width: 52px;
          height: 52px;
          border-radius: 10px;
          background-color: #F8FAFC;
          border: 1px solid #E2E8F0;
          display: flex;
          justify-content: center;
          align-items: center;
          flex-shrink: 0;
          overflow: hidden;
          padding: 4px;
          box-sizing: border-box;
        }

        .bb-bic-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          transition: filter 0.2s ease, opacity 0.2s ease;
        }

        .bb-bic-meta-col {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
        }

        .bb-bic-brand-tag {
          font-size: 10px;
          color: #64748B;
          text-transform: uppercase;
          font-weight: 800;
          letter-spacing: 0.3px;
        }

        .bb-bic-title {
          margin: 0;
          font-size: 13.5px;
          font-weight: 700;
          color: #0F172A;
          line-height: 17px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .bb-bic-unit-spec {
          font-size: 11.5px;
          color: #64748B;
          font-weight: 500;
        }

        .bb-bic-actions-cluster {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-shrink: 0;
        }

        .bb-bic-action-icon-btn {
          width: 30px;
          height: 30px;
          border-radius: 15px;
          background-color: #F8FAFC;
          border: 1px solid #E2E8F0;
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;
          padding: 0;
          transition: background-color 0.15s ease, transform 0.15s ease;
        }

        .bb-bic-action-icon-btn:hover {
          background-color: #F1F5F9;
        }

        .bb-bic-action-icon-btn:active {
          transform: scale(0.92);
        }

        .bb-bic-bottom-tier {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          padding-top: 2px;
        }

        .bb-bic-price-group {
          display: flex;
          align-items: baseline;
          gap: 6px;
          flex-wrap: wrap;
        }

        .bb-bic-final-price {
          font-size: 15px;
          font-weight: 800;
          color: #0F172A;
        }

        .bb-bic-mrp-price {
          font-size: 11.5px;
          color: #94A3B8;
          text-decoration: line-through;
        }

        .bb-bic-qty-badge {
          background-color: #F1F5F9;
          border: 1px solid #E2E8F0;
          color: #334155;
          border-radius: 6px;
          padding: 6px 12px;
          font-size: 13px;
          font-weight: 700;
          user-select: none;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .bb-bic-out-badge {
          background-color: #FEE2E2;
          border: 1px solid #FECACA;
          color: #DC2626;
          border-radius: 6px;
          padding: 4px 10px;
          font-size: 10.5px;
          font-weight: 800;
          letter-spacing: 0.4px;
          user-select: none;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        @media (max-width: 360px) {
          .bb-bucket-item-card {
            padding: 10px 10px;
          }
          .bb-bic-img-wrapper {
            width: 44px;
            height: 44px;
          }
          .bb-bic-title {
            font-size: 12.5px;
          }
        }
      `}</style>

      {/* Top Tier: Product Thumbnail, Title/Brand/Unit, Actions */}
      <div className="bb-bic-top-tier">
        <div className="bb-bic-img-wrapper">
          {imageUrl && !imgError ? (
            <img
              src={imageUrl}
              alt={name}
              className="bb-bic-img"
              style={{
                filter: isOutOfStock ? 'grayscale(80%)' : 'none',
                opacity: isOutOfStock ? 0.6 : 1,
              }}
              onError={() => setImgError(true)}
            />
          ) : (
            <img
              src={ToteIcon}
              alt="fallback"
              style={{ width: 22, height: 22, opacity: 0.35 }}
            />
          )}
        </div>

        <div className="bb-bic-meta-col">
          {brand && <span className="bb-bic-brand-tag">{brand}</span>}
          <h4
            className="bb-bic-title"
            title={name}
            style={{ color: isOutOfStock ? '#64748B' : '#0F172A' }}
          >
            {name}
          </h4>
          <span className="bb-bic-unit-spec">
            ₹{unitPrice.toLocaleString('en-IN')}{' '}
            {unitText ? `per ${unitText}` : 'per unit'}
          </span>
        </div>

        <div className="bb-bic-actions-cluster">
          <button
            className="bb-bic-action-icon-btn"
            onClick={handleBookmarkClick}
            disabled={isSaveLoading}
            type="button"
            title={localIsSaved ? 'Saved to Shortlist' : 'Save for later'}
          >
            <img
              src={localIsSaved ? FillBookmark : Bookmark}
              alt="save"
              style={{
                width: 14,
                height: 14,
                filter: localIsSaved
                  ? 'invert(16%) sepia(85%) saturate(6120%) hue-rotate(348deg) brightness(91%) contrast(106%)'
                  : 'none',
                transition: 'transform 0.15s ease',
              }}
            />
          </button>
        </div>
      </div>

      {/* Bottom Tier: Price breakdown & Static Quantity Badge */}
      <div className="bb-bic-bottom-tier">
        <div className="bb-bic-price-group">
          <span
            className="bb-bic-final-price"
            style={{
              color: isOutOfStock ? '#94A3B8' : '#0F172A',
              textDecoration: isOutOfStock ? 'line-through' : 'none',
            }}
          >
            ₹{itemTotal.toLocaleString('en-IN')}
          </span>
          {!isOutOfStock && itemMrpTotal > 0 && itemMrpTotal !== itemTotal && (
            <span className="bb-bic-mrp-price">
              ₹{itemMrpTotal.toLocaleString('en-IN')}
            </span>
          )}
        </div>

        {isOutOfStock ? (
          <div className="bb-bic-out-badge">
            OUT OF STOCK
          </div>
        ) : (
          <div className="bb-bic-qty-badge">
            Qty: {quantity}
          </div>
        )}
      </div>
    </div>
  );
}